// StaffPage.jsx
import React, { useState, useEffect } from 'react';
import StaffTable from './StaffTable';
import StaffDetailTable from './StaffDetailTable';
import CheckboxDropdown from '../../../../../components/CheckboxDropdown/CheckboxDropdown';
import { staffStatisticsActions } from './actions';
import { districts } from './personalConstants';
import { Form } from 'react-bootstrap';

const StaffPage = () => {
  const [selectedKindergarten, setSelectedKindergarten] = useState('');
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedPositionTypes, setSelectedPositionTypes] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]); // Новый фильтр пола
  const [positionSearch, setPositionSearch] = useState(''); // Новый фильтр поиска по должности
  const [searchTerm, setSearchTerm] = useState('');
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Використовуємо статичні константи замість завантаження з API
  const [availableDistricts] = useState(districts);
  
  // Опции для типов позиций
  const positionTypeOptions = [
    { value: 'pedagogical', label: 'Педагогічні' },
    { value: 'technical', label: 'Технічні' },
    { value: 'administrative', label: 'Адміністративні' },
    { value: 'service', label: 'Обслуговуючий персонал' }
  ];
  
  // Опции для статусов вакансий
  const statusOptions = [
    { value: 'vacant', label: 'Тільки з вакансіями' },
    { value: 'occupied', label: 'Тільки зайняті' }
  ];
  
  // Опции для пола работников
  const genderOptions = [
    { value: 'female', label: 'Жінки' },
    { value: 'male', label: 'Чоловіки' }
  ];

  // Завантаження даних штатного розкладу при зміні параметров
  useEffect(() => {
    if (selectedKindergarten || selectedDistricts.length > 0) {
      loadStaffData();
    } else {
      setApiData(null);
    }
  }, [
    selectedKindergarten, 
    selectedDistricts, 
    selectedPositionTypes, 
    selectedStatus, 
    selectedGenders,
    positionSearch
  ]);

  const loadStaffData = async () => {
    try {
      setLoading(true);
      setShowDetails(false);
      
      const params = {
        report_date: new Date().toISOString().split('T')[0]
      };

      if (selectedKindergarten) {
        params.kindergarten_id = selectedKindergarten;
      } else if (selectedDistricts.length > 0) {
        params.districts = selectedDistricts.join(',');
      }
      
      // Добавляем параметры фильтрации
      if (selectedPositionTypes.length > 0) {
        params.position_types = selectedPositionTypes.join(',');
      }
      
      if (selectedStatus.length > 0) {
        params.status = selectedStatus.join(',');
      }
      
      if (selectedGenders.length > 0) {
        params.genders = selectedGenders.join(',');
      }
      
      if (positionSearch) {
        params.position_search = positionSearch;
      }

      const response = await staffStatisticsActions.getStaffReport(params);
      
      if (response.data && response.data.success) {
        setApiData(response.data.data);
      } else {
        setApiData(null);
      }
    } catch (err) {
      console.error('Помилка завантаження штатного розкладу:', err);
      setApiData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleKindergartenSelect = (kindergartenId) => {
    setSelectedKindergarten(kindergartenId);
    setShowDetails(false);
  };

  // Обработчики для фильтра районов
  const toggleDistrict = (district) => {
    setSelectedDistricts(prev => {
      if (prev.includes(district)) {
        return prev.filter(d => d !== district);
      } else {
        return [...prev, district];
      }
    });
    setSelectedKindergarten('');
    setApiData(null);
    setShowDetails(false);
  };

  const selectAllDistricts = () => {
    setSelectedDistricts([...availableDistricts]);
    setSelectedKindergarten('');
    setApiData(null);
    setShowDetails(false);
  };

  const clearAllDistricts = () => {
    setSelectedDistricts([]);
    setSelectedKindergarten('');
    setApiData(null);
    setShowDetails(false);
  };

  // Обработчики для фильтра типов позиций
  const togglePositionType = (type) => {
    setSelectedPositionTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const selectAllPositionTypes = () => {
    setSelectedPositionTypes(positionTypeOptions.map(opt => opt.value));
  };

  const clearAllPositionTypes = () => {
    setSelectedPositionTypes([]);
  };

  // Обработчики для фильтра статусов
  const toggleStatus = (status) => {
    setSelectedStatus(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const selectAllStatus = () => {
    setSelectedStatus(statusOptions.map(opt => opt.value));
  };

  const clearAllStatus = () => {
    setSelectedStatus([]);
  };

  // Обработчики для фильтра пола
  const toggleGender = (gender) => {
    setSelectedGenders(prev => {
      if (prev.includes(gender)) {
        return prev.filter(g => g !== gender);
      } else {
        return [...prev, gender];
      }
    });
  };

  const selectAllGenders = () => {
    setSelectedGenders(genderOptions.map(opt => opt.value));
  };

  const clearAllGenders = () => {
    setSelectedGenders([]);
  };

  // Обработчик для поиска по должности
  const handlePositionSearchChange = (e) => {
    setPositionSearch(e.target.value);
  };

  // Обработчик сброса поиска по должности
  const clearPositionSearch = () => {
    setPositionSearch('');
  };

  const handleShowDetails = (kindergartenInfo) => {
    setSelectedKindergarten(kindergartenInfo.kindergartenId);
    setShowDetails(true);
  };

  const handleBackToList = () => {
    setShowDetails(false);
  };

  const handleBackToAggregated = () => {
    setSelectedKindergarten(''); // Сбрасываем выбранный садик
    // Фильтры и выбранные районы сохраняются
   };

  // Преобразуем районы в формат для компонента
  const districtOptions = availableDistricts.map(district => ({
    value: district,
    label: district
  }));

  // Отримуємо унікальні садочки з даних API
  const getUniqueKindergartens = () => {
    if (!apiData || !apiData.kindergartens) return [];
    
    return apiData.kindergartens.map(kg => ({
      id: kg.id,
      name: `ДНЗ №${kg.number} ${kg.name}`.trim(),
      number: kg.number
    }));
  };

  // Групування даних для агрегованого перегляду
  const getAggregatedData = () => {
    if (!apiData || !apiData.positions) return [];
    
    const groupedData = apiData.positions.reduce((acc, position) => {
      const kgInfo = position.createdInKindergarten;
      if (!kgInfo || !kgInfo.kindergartenId) return acc;

      const kgId = kgInfo.kindergartenId;
      const kgNumber = kgInfo.kindergartenNumber || 'Н/Д';
      const kgName = kgInfo.kindergartenName || 'Невідомий садочок';
      
      if (!acc[kgId]) {
        acc[kgId] = {
          kindergartenId: kgId,
          kindergartenNumber: kgNumber,
          kindergartenName: kgName,
          totals: {
            pedagogical: { female: 0, male: 0, total: 0, vacant: 0 },
            technical: { female: 0, male: 0, total: 0, vacant: 0 },
            administrative: { female: 0, male: 0, total: 0, vacant: 0 },
            service: { female: 0, male: 0, total: 0, vacant: 0 }
          }
        };
      }

      const positionType = position.positionType?.toLowerCase() || 'pedagogical';
      const typeKey = positionType.includes('педагог') ? 'pedagogical' :
                     positionType.includes('техніч') ? 'technical' :
                     positionType.includes('адмін') ? 'administrative' : 'service';

      acc[kgId].totals[typeKey].female += position.femaleCount || 0;
      acc[kgId].totals[typeKey].male += position.maleCount || 0;
      acc[kgId].totals[typeKey].total += position.totalUnits || 0;
      acc[kgId].totals[typeKey].vacant += position.vacantUnits || 0;

      return acc;
    }, {});

    return Object.values(groupedData);
  };

  // Фільтрація даних для детального перегляду
  const getDetailData = () => {
    if (!apiData || !apiData.positions) return [];
    
    const filtered = apiData.positions.filter(position => {
      const matches = position.createdInKindergarten?.kindergartenId === selectedKindergarten;
      return matches;
    });
    
    return filtered;
  };

  const aggregatedData = getAggregatedData();
  const detailData = getDetailData();
  const kindergartens = getUniqueKindergartens();

  // Отримуємо інформацію про поточний садочок для заголовка
  const getCurrentKindergartenInfo = () => {
    if (!selectedKindergarten || !apiData?.kindergartens) return null;
    
    return apiData.kindergartens.find(kg => kg.id === selectedKindergarten) || 
           aggregatedData.find(kg => kg.kindergartenId === selectedKindergarten);
  };

  const currentKindergartenInfo = getCurrentKindergartenInfo();

  // Конфигурации колонок
  const aggregatedColumns = [
    { key: 'kindergartenNumber', label: '№ садочка', sortable: true, width: '10%' },
    { key: 'kindergartenName', label: 'Назва садочка', sortable: true, width: '20%' },
    { 
      key: 'pedagogical', 
      label: 'Педагогічні', 
      sortable: false, 
      width: '17.5%',
      render: (value, row) => (
        <div>
          <div>Ж: {row.totals.pedagogical.female} Ч: {row.totals.pedagogical.male}</div>
          <div>Всього: {row.totals.pedagogical.total} Вакансій: <span className={row.totals.pedagogical.vacant > 0 ? 'staff-vacant-highlight' : ''}>{row.totals.pedagogical.vacant}</span></div>
        </div>
      )
    },
    { 
      key: 'technical', 
      label: 'Технічні', 
      sortable: false, 
      width: '17.5%',
      render: (value, row) => (
        <div>
          <div>Ж: {row.totals.technical.female} Ч: {row.totals.technical.male}</div>
          <div>Всього: {row.totals.technical.total} Вакансій: <span className={row.totals.technical.vacant > 0 ? 'staff-vacant-highlight' : ''}>{row.totals.technical.vacant}</span></div>
        </div>
      )
    },
    { 
      key: 'administrative', 
      label: 'Адміністративні', 
      sortable: false, 
      width: '17.5%',
      render: (value, row) => (
        <div>
          <div>Ж: {row.totals.administrative.female} Ч: {row.totals.administrative.male}</div>
          <div>Всього: {row.totals.administrative.total} Вакансій: <span className={row.totals.administrative.vacant > 0 ? 'staff-vacant-highlight' : ''}>{row.totals.administrative.vacant}</span></div>
        </div>
      )
    },
    { 
      key: 'service', 
      label: 'Обслуговуючий', 
      sortable: false, 
      width: '17.5%',
      render: (value, row) => (
        <div>
          <div>Ж: {row.totals.service.female} Ч: {row.totals.service.male}</div>
          <div>Всього: {row.totals.service.total} Вакансій: <span className={row.totals.service.vacant > 0 ? 'staff-vacant-highlight' : ''}>{row.totals.service.vacant}</span></div>
        </div>
      )
    }
  ];

  const detailColumns = [
    {
      key: 'positionName',
      label: 'Найменування посади',
      sortable: true,
      width: '35%'
    },
    {
      key: 'positionType',
      label: 'Тип посади',
      sortable: true,
      width: '15%'
    },
    {
      key: 'totalEmployees',
      label: 'Працівників',
      sortable: true,
      width: '10%'
    },
    {
      key: 'femaleCount',
      label: 'Жінок',
      sortable: true,
      width: '8%'
    },
    {
      key: 'maleCount',
      label: 'Чоловіків',
      sortable: true,
      width: '8%'
    },
    {
      key: 'totalUnits',
      label: 'Всього',
      sortable: true,
      width: '8%'
    },
    {
      key: 'occupiedUnits',
      label: 'Фактично у штаті',
      sortable: true,
      width: '8%'
    },
    {
      key: 'vacantUnits',
      label: 'Вакантних місць',
      sortable: true,
      width: '8%'
    }
  ];

  return (
    <div className="staff-page">
      <h2>Штатний розклад</h2>
      
      {/* Блок фильтров */}
      <div className="filters-row mb-4">
        <div className="row g-3">
          <div className="col-md-3">
            <CheckboxDropdown
              label="Райони:"
              options={districtOptions}
              selectedValues={selectedDistricts}
              onToggle={toggleDistrict}
              onSelectAll={selectAllDistricts}
              onClearAll={clearAllDistricts}
              placeholder="Оберіть райони"
              selectAllLabel="Вибрати всі райони"
              clearLabel="Скинути вибір районів"
            />
          </div>
          
          <div className="col-md-3">
            <CheckboxDropdown
              label="Типи посад:"
              options={positionTypeOptions}
              selectedValues={selectedPositionTypes}
              onToggle={togglePositionType}
              onSelectAll={selectAllPositionTypes}
              onClearAll={clearAllPositionTypes}
              placeholder="Оберіть типи посад"
              selectAllLabel="Вибрати всі типи"
              clearLabel="Скинути вибір типів"
            />
          </div>
          
          <div className="col-md-3">
            <CheckboxDropdown
              label="Статус:"
              options={statusOptions}
              selectedValues={selectedStatus}
              onToggle={toggleStatus}
              onSelectAll={selectAllStatus}
              onClearAll={clearAllStatus}
              placeholder="Оберіть статус"
              selectAllLabel="Вибрати всі статуси"
              clearLabel="Скинути вибір статусів"
            />
          </div>
          
          <div className="col-md-3">
            <CheckboxDropdown
              label="Стать працівників:"
              options={genderOptions}
              selectedValues={selectedGenders}
              onToggle={toggleGender}
              onSelectAll={selectAllGenders}
              onClearAll={clearAllGenders}
              placeholder="Оберіть стать"
              selectAllLabel="Вибрати всі статі"
              clearLabel="Скинути вибір статі"
            />
          </div>
          
          {/* Поиск по наименованию должности */}
          <div className="col-md-6">
            <div className="position-search-filter">
              <label className="form-label fw-semibold mb-2">
                Пошук за посадою:
              </label>
              <div className="position-search-input-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Введіть назву посади..."
                  value={positionSearch}
                  onChange={handlePositionSearchChange}
                  className="position-search-input"
                />
                {positionSearch && (
                  <button
                    type="button"
                    className="position-search-clear"
                    onClick={clearPositionSearch}
                    title="Очистити пошук"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {!showDetails ? (
         <StaffTable
          data={selectedKindergarten ? detailData : aggregatedData}
          columnsConfig={selectedKindergarten ? detailColumns : aggregatedColumns}
          kindergartens={kindergartens}
          selectedKindergarten={selectedKindergarten}
          onKindergartenSelect={handleKindergartenSelect}
          onSearch={handleSearch}
          onRowClick={selectedKindergarten ? null : (item) => handleShowDetails(item)}
          onBackToAggregated={handleBackToAggregated} // Новый проп
          loading={loading}
          isAggregatedView={!selectedKindergarten}
        />
      ) : (
        <StaffDetailTable
          data={detailData}
          kgNumber={currentKindergartenInfo?.number || currentKindergartenInfo?.kindergartenNumber}
          onBack={handleBackToList}
          loading={loading}
        />
      )}

      {/* Информация о загрузке */}
      {loading && (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Завантаження...</span>
          </div>
          <small className="text-muted">
            {selectedDistricts.length > 0 && !selectedKindergarten ? `Завантаження даних по ${selectedDistricts.length} районах...` : 'Завантаження даних...'}
          </small>
        </div>
      )}
    </div>
  );
};

export default StaffPage;